'use client';

import { FC } from 'react';
import { useSearchParams } from 'next/navigation';
import { ErrorIcon } from '@nx-frontend/shared/ui/icon/portal';
import { generalEnv } from '@nx-frontend/shared/env';
import { ErrorIcons } from '@nx-frontend/shared/types/error';

export type NoPageAccessErrorPageProps = {
  /** In some cases, the user doesn't have access to the page because the user is not authenticated on the org-specific domain.
   * Use this prop to redirect the user to the org-specific domain auth page to authenticate the user.
   * More details:
   * - https://nimbusweb.slack.com/archives/C03HUN51D9P/p1736773976020639
   * - https://nimbusweb.atlassian.net/browse/NIM-36273?focusedCommentId=152779
   * - https://gl.nimbusweb.co/web/auth-form/#%D0%BF%D0%B5%D1%80%D0%B5%D0%BD%D0%B0%D0%BF%D1%80%D0%B0%D0%B2%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5-%D0%BF%D0%BE%D0%BB%D1%8C%D0%B7%D0%BE%D0%B2%D0%B0%D1%82%D0%B5%D0%BB%D1%8F-%D0%B2-%D0%BD%D1%83%D0%B6%D0%BD%D1%83%D1%8E-%D0%BE%D1%80%D0%B3%D0%B0%D0%BD%D0%B8%D0%B7%D0%B0%D1%86%D0%B8%D1%8E-%D1%81-%D1%81%D0%BE%D1%85%D1%80%D0%B0%D0%BD%D0%B5%D0%BD%D0%B8%D0%B5%D0%BC-%D1%81%D0%B5%D1%81%D1%81%D0%B8%D0%B8
   **/
  tryOrgAuthFlow?: {
    orgId: string;
    domain: string | undefined;
    sub: string | undefined;
  };
};

export const skipRedirect = 'skip-try-org-auth-flow-redirect';

export const NoPageAccessErrorPage: FC<NoPageAccessErrorPageProps> = (props) => {
  const searchParams = useSearchParams();

  async function redirectUserToOrgAuthPage() {
    if (!props.tryOrgAuthFlow) {
      return;
    }

    const orgDomain = props.tryOrgAuthFlow.domain || `${props.tryOrgAuthFlow.sub}.${generalEnv.nimbusDomain}`;

    const successRedirectSearchParams = new URLSearchParams(window.location.search);
    successRedirectSearchParams.set(skipRedirect, 'true');

    let successRedirectUrl = `${window.location.protocol}//${window.location.host}${
      window.location.pathname
    }?${successRedirectSearchParams.toString()}`;

    // the auth-form service doesn't handle absolute URLs properly, so we need to remove the protocol
    successRedirectUrl = successRedirectUrl.replace(/^https?:/, '');

    const redirectUrl = `https://${orgDomain}/auth/open-org/${props.tryOrgAuthFlow.orgId}?success=${encodeURIComponent(
      successRedirectUrl
    )}`;

    window.location.href = redirectUrl;
  }

  if (props.tryOrgAuthFlow && !searchParams.get(skipRedirect)) {
    if (typeof window === 'undefined') {
      return null;
    }

    redirectUserToOrgAuthPage();

    return null;
  }

  return (
    <div className="w-full h-screen flex bg-white">
      <div className="lg:w-1/2 w-full flex justify-center items-center flex-col gap-8">
        <div className="w-full sm:w-1/2 lg:w-full xl:w-1/2 lg md:p-8 p-4 flex flex-col gap-8">
          <div className="flex flex-col items-center text-center gap-4">
            <h2
              data-cy="error-title"
              className="text-4xl text-center font-bold whitespace-pre-line [overflow-wrap:anywhere] xl:[overflow-wrap:initial]"
            >
              You don't have access to this page
            </h2>
          </div>
        </div>
      </div>
      <div data-cy="error-logo" className="lg:w-1/2 lg:block hidden w-full  py-6 pr-8">
        <div className="w-full h-full rounded-3xl flex justify-center items-center bg-error-layout">
          <ErrorIcon name={ErrorIcons.BLOCKED} width={200} height={200} />
        </div>
      </div>
    </div>
  );
};
